import React from 'react';
import { useLocalStorage } from 'hooks';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled, { createGlobalStyle } from 'styled-components';
import Icon from 'shared/components/Icon';
import { FreshLink } from 'shared/styles';
import { useAccessControl } from 'utils/access-control';
import {
  AccountInfoType,
  AccountUsersResponseType,
  UserType,
} from '../../../shared/types';
import { useGetAccountInfo } from '../../../shared/hooks';
import { useGetCurrentUser } from '../../screens/settings/hooks';
import { useGetAllUsers } from '../../screens/settings/screens/user/hooks';

const DrawerStyle = createGlobalStyle`
  .model-overlay.in-app-drawer {
    background: none !important;
  }
  .model-overlay.in-app-drawer .modal-content {
    left: 64px !important;
    height: calc(100vh - ${({ isVerified }) =>
      isVerified ? '60' : '88'}px) !important;
    top: ${({ isVerified }) => (isVerified ? '60' : '88')}px !important;
  }`;

type BannerType =
  | 'OWNER_ACTIVATION'
  | 'ADMIN_ACTIVATION'
  | 'USER_ACTIVATION'
  | 'DKIM_VERIFICATION';

const VerificationBanner = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [owner, setOwner] = React.useState<UserType>();
  const [banner, setBanner] = React.useState<BannerType>();
  const [accountInfo, setAccountInfo] = React.useState<AccountInfoType>();
  const [userInfo, setUserInfo] = React.useState<UserType>();
  const [dkimDiscardDate, setDkimDiscardDate] = useLocalStorage(
    'dkim-discard',
    undefined,
  );
  const { access = [] } = useAccessControl();
  const cacheAccountInfo =
    queryClient.getQueryData<AccountInfoType>('accountinfo');
  const cacheUserInfo = queryClient.getQueryData<UserType>('loggedInUser');
  useGetAccountInfo({
    callback: (isSuccess: boolean, response: AccountInfoType) => {
      if (isSuccess && response.id) {
        setAccountInfo(response);
      }
    },
    refetchOnWindowFocus:
      accountInfo?.status !== 'ACTIVATED' ||
      (!dkimDiscardDate && !accountInfo?.dkim_verified),
  });
  useGetCurrentUser({
    callback: (isSuccess: boolean, response: UserType) => {
      if (isSuccess && response.display_id) {
        setUserInfo(response);
      }
    },
    refetchOnWindowFocus: userInfo?.status !== 'ACTIVATED',
  });
  const { data: allUsers } = useGetAllUsers({
    enabled:
      !!accountInfo &&
      !!userInfo &&
      (accountInfo?.status !== 'ACTIVATED' || userInfo?.status !== 'ACTIVATED'),
    callback: (isSuccess: boolean, response: AccountUsersResponseType) => {
      if (isSuccess) {
        const ownerUser = response.account_users.find(
          (account_user) => account_user?.user_role === 'OWNER',
        );
        if (!owner && ownerUser && ownerUser.user_id && ownerUser.user)
          setOwner(ownerUser.user);
      }
    },
  });

  const getBanner = () => {
    switch (banner) {
      case 'OWNER_ACTIVATION': {
        return (
          <>
            {owner?.email ? (
              <Trans
                i18nKey="verification_banners.email_owner_activation"
                values={{
                  email: owner?.email,
                }}
                components={{ bold: <b /> }}
              />
            ) : (
              <Trans
                i18nKey="verification_banners.owner_activation"
                components={{ bold: <b /> }}
              />
            )}
          </>
        );
      }
      case 'ADMIN_ACTIVATION': {
        return (
          <>
            {owner?.email ? (
              <Trans
                i18nKey="verification_banners.email_admin_activation"
                values={{
                  email: owner?.email,
                }}
                components={{ bold: <b /> }}
              />
            ) : (
              <Trans
                i18nKey="verification_banners.admin_activation"
                components={{ bold: <b /> }}
              />
            )}
          </>
        );
      }
      case 'USER_ACTIVATION': {
        return (
          <>
            {userInfo?.email ? (
              <Trans
                i18nKey="verification_banners.email_user_activation"
                values={{
                  email: userInfo?.email,
                }}
                components={{ bold: <b /> }}
              />
            ) : (
              <Trans
                i18nKey="verification_banners.user_activation"
                components={{ bold: <b /> }}
              />
            )}
          </>
        );
      }
      case 'DKIM_VERIFICATION': {
        return (
          <>
            <Trans
              i18nKey="verification_banners.DKIM_verification"
              components={{
                bold: (
                  <FreshLink
                    href={`${window.location.origin}/app/settings/dkim`}
                    rel="noreferrer"
                  />
                ),
              }}
            />
          </>
        );
      }
    }
  };

  React.useEffect(() => {
    if (!accountInfo && cacheAccountInfo?.id) {
      setAccountInfo(cacheAccountInfo);
    }
  }, [cacheAccountInfo]);
  React.useEffect(() => {
    if (!userInfo && cacheUserInfo?.display_id) {
      setUserInfo(cacheUserInfo);
    }
  }, [cacheUserInfo]);
  React.useEffect(() => {
    if (!accountInfo || !userInfo || !allUsers) return;
    if (accountInfo?.status !== 'ACTIVATED') {
      if (owner?.email && userInfo?.email === owner?.email) {
        setBanner('OWNER_ACTIVATION');
        return;
      } else {
        setBanner('ADMIN_ACTIVATION');
        return;
      }
    } else {
      if (
        owner?.email &&
        userInfo?.email !== owner?.email &&
        userInfo?.status !== 'ACTIVATED'
      ) {
        setBanner('USER_ACTIVATION');
        return;
      }
      if (
        !accountInfo?.dkim_verified &&
        !dkimDiscardDate &&
        access.includes('settings:read')
      ) {
        setBanner('DKIM_VERIFICATION');
        return;
      }
    }
  }, [accountInfo, userInfo, allUsers]);

  return (
    <>
      <DrawerStyle isVerified={!banner} />
      {banner && (
        <article className="verification">
          <span className="banner-text">{getBanner()}</span>
          {!dkimDiscardDate && banner === 'DKIM_VERIFICATION' && (
            <CloseButton
              onClick={() => {
                setBanner(undefined);
                setDkimDiscardDate(new Date().toISOString());
              }}
            >
              <Icon glyph="close" height={12} width={16} viewBox="0 0 12 12" />
            </CloseButton>
          )}
        </article>
      )}
    </>
  );
};

export default VerificationBanner;

const CloseButton = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  color: #183247;
  display: grid;
  place-content: center;
  padding: 0 4px;
  cursor: pointer;
`;
